/**
 * Tree of checkboxes grouped by successive keys (region, country, store).
 * Controlled component: `selectedIds` in, `onChange(nextIds)` out. Keyboard and
 * screen-reader friendly by construction (native checkboxes, real buttons,
 * aria-expanded on branches).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  branchState,
  buildTree,
  collectLeaves,
  type TreeBranch,
  type TreeNode,
} from './tree';

// Material tree metrics reproduced: 48px rows, 30px indent per level, 40px toggle column.
const ROW_CLASS = 'flex min-h-[48px] items-center';
const INDENT_PX = 30;
const TOGGLE_PX = 40;
const CHECKBOX_CLASS = 'shrink-0';

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    aria-hidden="true"
    fill="currentColor"
    className={open ? 'rotate-90' : ''}
  >
    <path d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
  </svg>
);

export interface CheckboxTreeProps<Row extends Record<string, unknown>> {
  rows: readonly Row[];
  /** Grouping keys, outermost first, e.g. ['region', 'country']. */
  breakdown: readonly (keyof Row & string)[];
  idKey: keyof Row & string;
  labelKey: keyof Row & string;
  selectedIds: readonly (string | number)[];
  onChange: (selectedIds: (string | number)[]) => void;
  readOnly?: boolean;
  /** Optional content rendered at the right of a leaf row. */
  renderLeafRight?: (row: Row) => ReactNode;
  /** Optional content rendered at the right of a branch row (`level` starts at 0). */
  renderBranchRight?: (branch: TreeBranch<Row>, level: number) => ReactNode;
  /** Optional colour of a row label; `row` is set on leaves only. */
  nodeColor?: (node: {
    level: number;
    label: string;
    row?: Row;
  }) => string | undefined;
  /** Branches start collapsed like the Material tree did. */
  defaultExpanded?: boolean;
  className?: string;
  testId?: string | undefined;
}

const rowId = <Row extends Record<string, unknown>>(
  row: Row,
  idKey: keyof Row & string,
): string | number => row[idKey] as string | number;

export function CheckboxTree<Row extends Record<string, unknown>>({
  rows,
  breakdown,
  idKey,
  labelKey,
  selectedIds,
  onChange,
  readOnly = false,
  renderLeafRight,
  renderBranchRight,
  nodeColor,
  defaultExpanded = false,
  className,
  testId,
}: Readonly<CheckboxTreeProps<Row>>) {
  const tree = useMemo(
    () => buildTree(rows, breakdown, idKey),
    [rows, breakdown, idKey],
  );
  const selected = useMemo(
    () => new Set<string | number>(selectedIds),
    [selectedIds],
  );
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const isSelected = (row: Row) => selected.has(rowId(row, idKey));
  const subId = (suffix: string) =>
    testId ? `${testId}-${suffix}` : undefined;

  const emit = (next: Set<string | number>) => {
    // Keep the order of `rows` so the output is stable.
    onChange(rows.map((row) => rowId(row, idKey)).filter((id) => next.has(id)));
  };

  const toggleLeaf = (row: Row) => {
    const next = new Set(selected);
    const id = rowId(row, idKey);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    emit(next);
  };

  const toggleBranch = (branch: TreeBranch<Row>) => {
    const leaves = collectLeaves(branch);
    const next = new Set(selected);
    const allSelected = branchState(branch, isSelected) === 'checked';
    leaves.forEach((row) => {
      const id = rowId(row, idKey);
      if (allSelected) next.delete(id);
      else next.add(id);
    });
    emit(next);
  };

  const isExpanded = (key: string) =>
    expanded.has(key) ? !defaultExpanded : defaultExpanded;

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const renderNodes = (nodes: TreeNode<Row>[], level: number) => (
    <ul role={level === 0 ? 'tree' : 'group'} className="m-0 list-none p-0">
      {nodes.map((node) => {
        if (node.kind === 'leaf') {
          const label = String(node.row[labelKey] ?? '');
          return (
            <li
              key={node.key}
              role="treeitem"
              aria-selected={isSelected(node.row)}
              className={ROW_CLASS}
              style={{ paddingLeft: `${level * INDENT_PX + TOGGLE_PX}px` }}
              data-testid={subId('leaf')}
              data-label={label}
            >
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={isSelected(node.row)}
                  disabled={readOnly}
                  onChange={() => toggleLeaf(node.row)}
                  className={CHECKBOX_CLASS}
                  data-testid={subId('checkbox')}
                />
                <span
                  // A disabled row greys its label over the row colour.
                  style={{
                    color: readOnly
                      ? undefined
                      : nodeColor?.({ level, label, row: node.row }),
                  }}
                >
                  {label}
                </span>
              </label>
              {renderLeafRight?.(node.row)}
            </li>
          );
        }
        const state = branchState(node, isSelected);
        const open = isExpanded(node.key);
        return (
          <li
            key={node.key}
            role="treeitem"
            aria-expanded={open}
            aria-selected={state === 'checked'}
            style={{ paddingLeft: `${level * INDENT_PX}px` }}
            data-testid={subId('branch')}
            data-label={node.label}
          >
            <div className={ROW_CLASS}>
              <button
                type="button"
                aria-label={`toggle ${node.label}`}
                aria-expanded={open}
                onClick={() => toggleExpanded(node.key)}
                className="flex h-10 w-10 shrink-0 items-center justify-center text-black"
                data-testid={subId('toggle-button')}
              >
                <Chevron open={open} />
              </button>
              <label className="flex cursor-pointer items-center gap-2">
                <IndeterminateCheckbox
                  state={state}
                  disabled={readOnly}
                  onChange={() => toggleBranch(node)}
                  testId={subId('checkbox')}
                />
                <span
                  style={{
                    color: readOnly
                      ? undefined
                      : nodeColor?.({ level, label: node.label }),
                  }}
                >
                  {node.label}
                </span>
              </label>
              {renderBranchRight?.(node, level)}
            </div>
            {open && renderNodes(node.children, level + 1)}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div
      className={`checkbox-tree ${className ?? ''}`}
      onClick={(event) => event.stopPropagation()}
      role="presentation"
    >
      {renderNodes(tree, 0)}
    </div>
  );
}

function IndeterminateCheckbox({
  state,
  disabled,
  onChange,
  testId,
}: Readonly<{
  state: 'checked' | 'unchecked' | 'indeterminate';
  disabled: boolean;
  onChange: () => void;
  testId?: string | undefined;
}>) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'indeterminate';
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === 'checked'}
      aria-checked={state === 'indeterminate' ? 'mixed' : state === 'checked'}
      disabled={disabled}
      onChange={onChange}
      className={CHECKBOX_CLASS}
      data-testid={testId}
    />
  );
}
